import { Maximize2, Minimize2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/* ENLARGE — fullscreen toggle for game frames. Uses the Fullscreen API where it
   exists (desktop, Android, iPad); falls back to a CSS "zoom" overlay on devices
   that refuse element fullscreen (iPhone Safari). Self-anchoring: with no
   targetRef it enlarges its parent element, so drop it as the frame's first child. */
export function GameFullscreenButton({ targetRef, testid }) {
  const [on, setOn] = useState(false);
  const selfRef = useRef(null);
  const target = () => targetRef?.current || selfRef.current?.parentElement;

  useEffect(() => {
    const sync = () => {
      const el = target();
      const fs = document.fullscreenElement || document.webkitFullscreenElement;
      setOn(!!el && (fs === el || el.classList.contains("game-zoomed")));
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef]);

  const toggle = async () => {
    const el = target();
    if (!el) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl === el) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) exit.call(document);
      return;
    }
    if (el.classList.contains("game-zoomed")) {
      el.classList.remove("game-zoomed");
      document.body.classList.remove("game-zoom-lock");
      setOn(false);
      return;
    }
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      try {
        await req.call(el);
        setOn(true);
        return;
      } catch {
        /* fullscreen refused (iPhone etc.) — fall through to the zoom overlay */
      }
    }
    el.classList.add("game-zoomed");
    document.body.classList.add("game-zoom-lock");
    setOn(true);
  };

  return (
    <button
      ref={selfRef}
      type="button"
      className="game-fullscreen-btn"
      onClick={toggle}
      data-testid={testid}
      aria-label={on ? "Shrink the game screen" : "Enlarge the game screen"}
    >
      {on ? <Minimize2 size={13} /> : <Maximize2 size={13} />} {on ? "SHRINK" : "ENLARGE"}
    </button>
  );
}
